import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Account, Transaction, CreditCard, LoanApplication, Loan, BankingContextType, User } from '../types';
import { 
  mockAccounts, 
  mockTransactions, 
  mockCreditCards, 
  mockLoanApplications, 
  mockLoans,
  mockUsers,
  generateAccountNumber,
  generateCardNumber 
} from '../utils/mockData';
import { useAuth } from './AuthContext';
import { v4 as uuidv4 } from 'uuid';

const BankingContext = createContext<BankingContextType | undefined>(undefined);

export const useBanking = (): BankingContextType => {
  const context = useContext(BankingContext);
  if (!context) {
    throw new Error('useBanking must be used within a BankingProvider');
  }
  return context;
};

interface BankingProviderProps {
  children: ReactNode;
}

export const BankingProvider: React.FC<BankingProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<Account[]>(mockAccounts);
  const [transactions, setTransactions] = useState<Transaction[]>(mockTransactions);
  const [creditCards, setCreditCards] = useState<CreditCard[]>(mockCreditCards);
  const [loanApplications, setLoanApplications] = useState<LoanApplication[]>(mockLoanApplications);
  const [loans, setLoans] = useState<Loan[]>(mockLoans);
  const [allUsers, setAllUsers] = useState<User[]>([]);

  useEffect(() => {
    // Load data from localStorage
    const loadFromStorage = (key: string, defaultValue: any[]) => {
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Convert date strings back to Date objects
        return parsed.map((item: any) => ({
          ...item,
          createdAt: item.createdAt ? new Date(item.createdAt) : undefined,
          timestamp: item.timestamp ? new Date(item.timestamp) : undefined,
          appliedAt: item.appliedAt ? new Date(item.appliedAt) : undefined,
          reviewedAt: item.reviewedAt ? new Date(item.reviewedAt) : undefined,
        }));
      }
      return defaultValue;
    };

    setAccounts(loadFromStorage('bankingAccounts', mockAccounts));
    setTransactions(loadFromStorage('bankingTransactions', mockTransactions));
    setCreditCards(loadFromStorage('bankingCreditCards', mockCreditCards));
    setLoanApplications(loadFromStorage('bankingLoanApplications', mockLoanApplications));
    setLoans(loadFromStorage('bankingLoans', mockLoans));

    // Load all users for admin functionality
    const storedUsers = localStorage.getItem('allUsers');
    if (storedUsers) {
      const parsed = JSON.parse(storedUsers);
      // Convert date strings back to Date objects for users
      const usersWithDates = parsed.map((user: any) => ({
        ...user,
        createdAt: user.createdAt ? new Date(user.createdAt) : new Date(),
      }));
      setAllUsers(usersWithDates);
    } else {
      // Initialize with mock users if no data in localStorage
      setAllUsers(mockUsers);
      localStorage.setItem('allUsers', JSON.stringify(mockUsers));
    }
  }, []);

  const saveToStorage = (key: string, data: any[]) => {
    localStorage.setItem(key, JSON.stringify(data));
  };

  const createAccount = async (accountType: 'checking' | 'savings'): Promise<void> => {
    if (!user) throw new Error('User must be logged in');

    const newAccount: Account = {
      id: uuidv4(),
      userId: user.id,
      accountNumber: generateAccountNumber(),
      accountType,
      balance: 0,
      status: 'active',
      createdAt: new Date(),
    };

    const updatedAccounts = [...accounts, newAccount];
    setAccounts(updatedAccounts);
    saveToStorage('bankingAccounts', updatedAccounts);
  };

  const deposit = async (accountId: string, amount: number): Promise<void> => {
    if (!user) throw new Error('User must be logged in');

    // Validate amount
    if (amount <= 0) {
      throw new Error('Deposit amount must be greater than zero');
    }
    if (amount > 1000000) {
      throw new Error('Deposit amount cannot exceed $1,000,000');
    }
    if (!Number.isFinite(amount)) {
      throw new Error('Invalid deposit amount');
    }

    const account = accounts.find(acc => acc.id === accountId);
    if (!account) {
      throw new Error('Account not found');
    }
    if (account.userId !== user.id) {
      throw new Error('You can only deposit to your own accounts');
    }
    if (account.status === 'blocked') {
      throw new Error('Cannot deposit to a blocked account');
    }

    try {
      const updatedAccounts = accounts.map(acc => 
        acc.id === accountId ? { ...acc, balance: acc.balance + amount } : acc
      );

      const newTransaction: Transaction = {
        id: uuidv4(),
        fromAccountId: accountId,
        amount,
        type: 'deposit',
        description: 'Deposit',
        timestamp: new Date(),
      };

      setAccounts(updatedAccounts);
      const updatedTransactions = [...transactions, newTransaction];
      setTransactions(updatedTransactions);
      
      saveToStorage('bankingAccounts', updatedAccounts);
      saveToStorage('bankingTransactions', updatedTransactions);
    } catch (storageError) {
      // If there's an error saving to storage, throw a more specific error
      console.error('Storage error during deposit:', storageError);
      throw new Error('Failed to save deposit - please try again');
    }
  };

  const transfer = async (
    fromAccountId: string, 
    toAccountId: string, 
    amount: number, 
    recipientEmail?: string
  ): Promise<void> => {
    if (!user) throw new Error('User must be logged in');

    const fromAccount = accounts.find(acc => acc.id === fromAccountId);
    const toAccount = accounts.find(acc => acc.id === toAccountId);
    
    if (!fromAccount || fromAccount.userId !== user.id) {
      throw new Error('Source account not found');
    }
    if (!toAccount) {
      throw new Error('Destination account not found');
    }
    if (fromAccount.status === 'blocked') {
      throw new Error('Source account is blocked');
    }
    if (fromAccount.balance < amount) {
      throw new Error('Insufficient funds');
    }

    const updatedAccounts = accounts.map(acc => {
      if (acc.id === fromAccountId) {
        return { ...acc, balance: acc.balance - amount };
      }
      if (acc.id === toAccountId) {
        return { ...acc, balance: acc.balance + amount };
      }
      return acc;
    });

    const newTransaction: Transaction = {
      id: uuidv4(),
      fromAccountId,
      toAccountId,
      amount,
      type: 'transfer',
      description: recipientEmail ? `Transfer to ${recipientEmail}` : 'Internal Transfer',
      timestamp: new Date(),
      recipientEmail,
    };

    setAccounts(updatedAccounts);
    const updatedTransactions = [...transactions, newTransaction];
    setTransactions(updatedTransactions);
    
    saveToStorage('bankingAccounts', updatedAccounts);
    saveToStorage('bankingTransactions', updatedTransactions);
  };

  const applyCreditCard = async (cardType: 'platinum' | 'gold' | 'standard'): Promise<void> => {
    if (!user) throw new Error('User must be logged in');

    const creditLimits = {
      platinum: 15000,
      gold: 10000,
      standard: 5000,
    };

    const newCreditCard: CreditCard = {
      id: uuidv4(),
      userId: user.id,
      cardNumber: generateCardNumber(),
      cardType,
      creditLimit: creditLimits[cardType],
      currentBalance: 0,
      status: 'active',
      expiryDate: (() => {
        const futureDate = new Date(Date.now() + 3 * 365 * 24 * 60 * 60 * 1000);
        const month = String(futureDate.getMonth() + 1).padStart(2, '0');
        const year = String(futureDate.getFullYear()).slice(-2);
        return `${month}/${year}`;
      })(),
      createdAt: new Date(),
    };

    const updatedCreditCards = [...creditCards, newCreditCard];
    setCreditCards(updatedCreditCards);
    saveToStorage('bankingCreditCards', updatedCreditCards);
  };

  const applyLoan = async (loanType: 'personal' | 'auto', amount: number, purpose: string): Promise<void> => {
    if (!user) throw new Error('User must be logged in');

    const newLoanApplication: LoanApplication = {
      id: uuidv4(),
      userId: user.id,
      loanType,
      amount,
      purpose,
      status: 'pending',
      appliedAt: new Date(),
    };

    const updatedLoanApplications = [...loanApplications, newLoanApplication];
    setLoanApplications(updatedLoanApplications);
    saveToStorage('bankingLoanApplications', updatedLoanApplications);
  };

  const blockAccount = async (accountId: string): Promise<void> => {
    const updatedAccounts = accounts.map(acc => 
      acc.id === accountId ? { ...acc, status: 'blocked' as const } : acc
    );
    setAccounts(updatedAccounts);
    saveToStorage('bankingAccounts', updatedAccounts);
  };

  const unblockAccount = async (accountId: string): Promise<void> => {
    const updatedAccounts = accounts.map(acc => 
      acc.id === accountId ? { ...acc, status: 'active' as const } : acc
    );
    setAccounts(updatedAccounts);
    saveToStorage('bankingAccounts', updatedAccounts);
  };

  const blockCreditCard = async (cardId: string): Promise<void> => {
    const updatedCreditCards = creditCards.map(card => 
      card.id === cardId ? { ...card, status: 'blocked' as const } : card
    );
    setCreditCards(updatedCreditCards);
    saveToStorage('bankingCreditCards', updatedCreditCards);
  };

  const unblockCreditCard = async (cardId: string): Promise<void> => {
    const updatedCreditCards = creditCards.map(card => 
      card.id === cardId ? { ...card, status: 'active' as const } : card
    );
    setCreditCards(updatedCreditCards);
    saveToStorage('bankingCreditCards', updatedCreditCards);
  };

  const approveLoan = async (applicationId: string): Promise<void> => {
    const application = loanApplications.find(app => app.id === applicationId);
    if (!application) throw new Error('Application not found');

    // Update application status
    const updatedApplications = loanApplications.map(app => 
      app.id === applicationId 
        ? { ...app, status: 'approved' as const, reviewedAt: new Date(), reviewedBy: user?.id }
        : app
    );

    // Create loan account
    const loanAccount: Account = {
      id: uuidv4(),
      userId: application.userId,
      accountNumber: generateAccountNumber(),
      accountType: 'loan',
      balance: application.amount,
      status: 'active',
      createdAt: new Date(),
    };

    // Create loan
    const newLoan: Loan = {
      id: uuidv4(),
      userId: application.userId,
      applicationId,
      accountId: loanAccount.id,
      loanType: application.loanType,
      principalAmount: application.amount,
      currentBalance: application.amount,
      interestRate: application.loanType === 'personal' ? 8.5 : 6.5,
      termMonths: application.loanType === 'personal' ? 36 : 60,
      monthlyPayment: 0, // Calculate based on amount and terms
      status: 'active',
      createdAt: new Date(),
    };

    // Find user's checking account to deposit funds
    const userCheckingAccount = accounts.find(acc => 
      acc.userId === application.userId && acc.accountType === 'checking'
    );

    if (userCheckingAccount) {
      // Deposit loan amount to checking account
      const updatedAccounts = accounts.map(acc => 
        acc.id === userCheckingAccount.id 
          ? { ...acc, balance: acc.balance + application.amount }
          : acc
      );
      updatedAccounts.push(loanAccount);

      const depositTransaction: Transaction = {
        id: uuidv4(),
        fromAccountId: loanAccount.id,
        toAccountId: userCheckingAccount.id,
        amount: application.amount,
        type: 'loan_disbursement',
        description: `${application.loanType} loan disbursement`,
        timestamp: new Date(),
      };

      setAccounts(updatedAccounts);
      const updatedTransactions = [...transactions, depositTransaction];
      setTransactions(updatedTransactions);
      saveToStorage('bankingAccounts', updatedAccounts);
      saveToStorage('bankingTransactions', updatedTransactions);
    }

    setLoanApplications(updatedApplications);
    const updatedLoans = [...loans, newLoan];
    setLoans(updatedLoans);
    saveToStorage('bankingLoanApplications', updatedApplications);
    saveToStorage('bankingLoans', updatedLoans);
  };

  const declineLoan = async (applicationId: string): Promise<void> => {
    const updatedApplications = loanApplications.map(app => 
      app.id === applicationId 
        ? { ...app, status: 'declined' as const, reviewedAt: new Date(), reviewedBy: user?.id }
        : app
    );
    setLoanApplications(updatedApplications);
    saveToStorage('bankingLoanApplications', updatedApplications);
  };

  const refreshData = async (): Promise<void> => {
    // This could trigger a refresh from an API in a real application
    // For now, we'll just reload from localStorage
    const storedUsers = localStorage.getItem('allUsers');
    if (storedUsers) {
      const parsed = JSON.parse(storedUsers);
      // Convert date strings back to Date objects for users
      const usersWithDates = parsed.map((user: any) => ({
        ...user,
        createdAt: user.createdAt ? new Date(user.createdAt) : new Date(),
      }));
      setAllUsers(usersWithDates);
    }
  };

  const value: BankingContextType = {
    // Filter data by current user (except for admin users who can see everything for administrative purposes)
    accounts: user?.role === 'admin' ? accounts : accounts.filter(acc => acc.userId === user?.id),
    transactions: user?.role === 'admin' 
      ? transactions 
      : transactions.filter(txn => {
          // Include transactions where the user owns either the source or destination account
          const userAccountIds = accounts.filter(acc => acc.userId === user?.id).map(acc => acc.id);
          return userAccountIds.includes(txn.fromAccountId) || 
                 (txn.toAccountId && userAccountIds.includes(txn.toAccountId));
        }),
    creditCards: user?.role === 'admin' ? creditCards : creditCards.filter(cc => cc.userId === user?.id),
    loanApplications: user?.role === 'admin' ? loanApplications : loanApplications.filter(app => app.userId === user?.id),
    loans: user?.role === 'admin' ? loans : loans.filter(loan => loan.userId === user?.id),
    allUsers,
    createAccount,
    deposit,
    transfer,
    applyCreditCard,
    applyLoan,
    blockAccount,
    unblockAccount,
    blockCreditCard,
    unblockCreditCard,
    approveLoan,
    declineLoan,
    refreshData,
  };

  return (
    <BankingContext.Provider value={value}>
      {children}
    </BankingContext.Provider>
  );
}; 